/**
 * CalendarPage - Calendar view with tour stops and bookings
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import type {
  EventClickArg,
  EventInput,
  DatesSetArg,
  EventContentArg,
  MoreLinkArg,
} from '@fullcalendar/core';
import {
  getCalendarData,
  getShows,
  type Show,
  type CalendarTourStop,
  type CalendarClass,
} from '../../../api/admin';

// Refined show color palette — muted, sophisticated tones
const SHOW_COLORS = [
  { bg: '#3B82F6', light: '#EFF6FF', border: '#2563EB', text: '#1E40AF' },
  { bg: '#10B981', light: '#ECFDF5', border: '#059669', text: '#065F46' },
  { bg: '#F59E0B', light: '#FFFBEB', border: '#D97706', text: '#92400E' },
  { bg: '#EF4444', light: '#FEF2F2', border: '#DC2626', text: '#991B1B' },
  { bg: '#8B5CF6', light: '#F5F3FF', border: '#7C3AED', text: '#5B21B6' },
  { bg: '#EC4899', light: '#FDF2F8', border: '#DB2777', text: '#9D174D' },
  { bg: '#14B8A6', light: '#F0FDFA', border: '#0D9488', text: '#115E59' },
  { bg: '#F97316', light: '#FFF7ED', border: '#EA580C', text: '#9A3412' },
  { bg: '#6366F1', light: '#EEF2FF', border: '#4F46E5', text: '#3730A3' },
  { bg: '#84CC16', light: '#F7FEE7', border: '#65A30D', text: '#3F6212' },
];

// All 7 booking_status enum values, in pg_enum order (see BookingStatus in @sda/shared)
const BOOKING_STATUS_COLORS: Record<
  string,
  { bg: string; light: string; border: string; text: string }
> = {
  pending: { bg: '#F59E0B', light: '#FFFBEB', border: '#D97706', text: '#92400E' },
  deposit_paid: { bg: '#6366F1', light: '#EEF2FF', border: '#4F46E5', text: '#3730A3' },
  confirmed: { bg: '#10B981', light: '#ECFDF5', border: '#059669', text: '#065F46' },
  balance_paid: { bg: '#14B8A6', light: '#F0FDFA', border: '#0D9488', text: '#115E59' },
  completed: { bg: '#3B82F6', light: '#EFF6FF', border: '#2563EB', text: '#1E40AF' },
  cancelled: { bg: '#9CA3AF', light: '#F9FAFB', border: '#6B7280', text: '#374151' },
  refunded: { bg: '#EF4444', light: '#FEF2F2', border: '#DC2626', text: '#991B1B' },
};

const BOOKING_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  deposit_paid: 'Deposit Paid',
  confirmed: 'Confirmed',
  balance_paid: 'Balance Paid',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

function statusLabelFor(status: string): string {
  return BOOKING_STATUS_LABELS[status] || status;
}

type ViewMode = 'tour_stops' | 'bookings';

interface SelectedEvent {
  type: 'tour_stop' | 'class';
  tourStop?: CalendarTourStop;
  classItem?: CalendarClass;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(time: string | null): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours!, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function getInitialDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start);
  end.setDate(end.getDate() + 42);
  return {
    start: start.toISOString().split('T')[0]!,
    end: end.toISOString().split('T')[0]!,
  };
}

export default function CalendarPage() {
  const [shows, setShows] = useState<Show[]>([]);
  const [tourStops, setTourStops] = useState<CalendarTourStop[]>([]);
  const [classes, setClasses] = useState<CalendarClass[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedShowId, setSelectedShowId] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('bookings');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(getInitialDateRange);
  const [selectedEvent, setSelectedEvent] = useState<SelectedEvent | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    getShows().then(setShows).catch(console.error);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getCalendarData({
          start_date: dateRange.start,
          end_date: dateRange.end,
          show_id: selectedShowId || undefined,
        });
        if (!cancelled) {
          setTourStops(data.tourStops);
          setClasses(data.classes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load calendar data');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dateRange, selectedShowId]);

  const showColorMap = useMemo(() => {
    const map = new Map<string, (typeof SHOW_COLORS)[0]>();
    shows.forEach((show, i) => {
      map.set(show.id, SHOW_COLORS[i % SHOW_COLORS.length]!);
    });
    return map;
  }, [shows]);

  const bookingsByTourStop = useMemo(() => {
    const map = new Map<
      string,
      Map<string, { booking: NonNullable<CalendarClass['bookings']>; classes: CalendarClass[] }>
    >();

    for (const cls of classes) {
      if (!cls.bookings) continue;
      if (!map.has(cls.tour_stop_id)) map.set(cls.tour_stop_id, new Map());
      const tourStopMap = map.get(cls.tour_stop_id)!;
      const bookingId = cls.bookings.id;
      if (!tourStopMap.has(bookingId)) {
        tourStopMap.set(bookingId, { booking: cls.bookings, classes: [] });
      }
      tourStopMap.get(bookingId)!.classes.push(cls);
    }

    return map;
  }, [classes]);

  // Tour stop events
  const tourStopEvents: EventInput[] = useMemo(() => {
    return tourStops.map((stop) => {
      const colors = showColorMap.get(stop.show_id) || SHOW_COLORS[0]!;
      const endDate = new Date(stop.end_date);
      endDate.setDate(endDate.getDate() + 1);

      const bookingCount = bookingsByTourStop.get(stop.id)?.size || 0;

      return {
        id: `ts-${stop.id}`,
        title: stop.shows?.name || 'Unknown',
        start: stop.start_date,
        end: endDate.toISOString().split('T')[0],
        display: 'block',
        extendedProps: {
          type: 'tour_stop',
          data: stop,
          colors,
          cityName: stop.cities?.name || '',
          bookingCount,
          isSoldOut: stop.is_sold_out,
        },
      };
    });
  }, [tourStops, showColorMap, bookingsByTourStop]);

  // Booking events
  const bookingEvents: EventInput[] = useMemo(() => {
    return classes.map((cls) => {
      const booking = cls.bookings;
      const statusKey = booking?.status || 'pending';
      const colors =
        BOOKING_STATUS_COLORS[statusKey] || BOOKING_STATUS_COLORS.pending!;
      const studioName = booking?.studios?.name || 'Unknown Studio';
      const statusLabel = statusLabelFor(statusKey);

      return {
        id: `cls-${cls.id}`,
        title: studioName,
        start: cls.class_date,
        display: 'block',
        extendedProps: {
          type: 'class',
          data: cls,
          colors,
          statusLabel,
          statusKey,
        },
      };
    });
  }, [classes]);

  const filteredBookingEvents = useMemo(() => {
    if (!statusFilter) return bookingEvents;
    return bookingEvents.filter(
      (e) => (e.extendedProps as Record<string, unknown>).statusKey === statusFilter,
    );
  }, [bookingEvents, statusFilter]);

  const events = useMemo(() => {
    return viewMode === 'tour_stops' ? tourStopEvents : filteredBookingEvents;
  }, [viewMode, tourStopEvents, filteredBookingEvents]);

  // Custom event rendering
  const renderEventContent = useCallback(
    (arg: EventContentArg) => {
      const props = arg.event.extendedProps;

      if (props.type === 'tour_stop') {
        const colors = props.colors as (typeof SHOW_COLORS)[0];
        const bookingCount = props.bookingCount as number;
        const cityName = props.cityName as string;
        const isSoldOut = props.isSoldOut as boolean;
        const tourData = props.data as CalendarTourStop;
        const dateRange = `${formatShortDate(tourData.start_date)} – ${formatShortDate(tourData.end_date)}`;

        return (
          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-all hover:shadow-sm overflow-hidden"
            style={{
              backgroundColor: colors.light,
              borderLeft: `3px solid ${colors.bg}`,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: colors.bg }}
            />
            <span
              className="text-[11px] font-semibold truncate"
              style={{ color: colors.text }}
            >
              {arg.event.title}
            </span>
            {cityName && (
              <span
                className="text-[10px] truncate opacity-70 hidden sm:inline"
                style={{ color: colors.text }}
              >
                — {cityName}
              </span>
            )}
            <span
              className="text-[9px] opacity-50 hidden md:inline flex-shrink-0"
              style={{ color: colors.text }}
            >
              {dateRange}
            </span>
            <span className="ml-auto flex items-center gap-1 flex-shrink-0">
              {isSoldOut && (
                <span className="text-[9px] font-bold px-1 py-px rounded bg-red-100 text-red-700">
                  SOLD OUT
                </span>
              )}
              {bookingCount > 0 && (
                <span
                  className="text-[9px] font-bold px-1.5 py-px rounded-full"
                  style={{
                    backgroundColor: colors.bg,
                    color: 'white',
                  }}
                >
                  {bookingCount}
                </span>
              )}
            </span>
          </div>
        );
      }

      // Booking/class event
      const colors = props.colors as (typeof BOOKING_STATUS_COLORS)['pending'];
      const statusLabel = props.statusLabel as string;

      return (
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-all hover:shadow-sm overflow-hidden"
          style={{
            backgroundColor: colors.light,
            borderLeft: `3px solid ${colors.bg}`,
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: colors.bg }}
          />
          <span
            className="text-[11px] font-medium truncate"
            style={{ color: colors.text }}
          >
            {arg.event.title}
          </span>
          <span
            className="ml-auto text-[9px] font-semibold px-1.5 py-px rounded-full flex-shrink-0"
            style={{ backgroundColor: colors.bg, color: 'white' }}
          >
            {statusLabel}
          </span>
        </div>
      );
    },
    [],
  );

  const handleDatesSet = useCallback((info: DatesSetArg) => {
    const start = info.startStr;
    const end = info.endStr;
    setDateRange((prev) => {
      if (prev?.start === start && prev?.end === end) return prev;
      return { start, end };
    });
  }, []);

  const handleEventClick = useCallback((info: EventClickArg) => {
    const { type, data } = info.event.extendedProps;
    if (type === 'tour_stop') {
      setSelectedEvent({ type: 'tour_stop', tourStop: data as CalendarTourStop });
    } else {
      setSelectedEvent({ type: 'class', classItem: data as CalendarClass });
    }
  }, []);

  const handleDateClick = useCallback(
    (info: { dateStr: string }) => {
      setSelectedDay(info.dateStr);
    },
    [],
  );

  // "+N more" opens our day popup instead of FullCalendar's popover, which
  // gets clipped by the calendar's overflow container. Returning a non-view
  // string suppresses the popover (zoomTo no-ops on unknown view names).
  const handleMoreLinkClick = useCallback((arg: MoreLinkArg) => {
    setSelectedDay(arg.date.toISOString().split('T')[0]!);
    return 'none';
  }, []);

  // Events for the selected day
  const dayTourStops = useMemo(() => {
    if (!selectedDay) return [];
    return tourStops.filter(
      (ts) => ts.start_date <= selectedDay && ts.end_date >= selectedDay,
    );
  }, [selectedDay, tourStops]);

  const dayBookings = useMemo(() => {
    if (!selectedDay) return [];
    const bMap = new Map<
      string,
      {
        id: string;
        booking_number: string;
        status: string;
        studioName: string;
        classes: { startTime: string | null; endTime: string | null; level: string | null }[];
      }
    >();
    for (const cls of classes) {
      if (cls.class_date !== selectedDay || !cls.bookings) continue;
      const b = cls.bookings;
      if (!bMap.has(b.id)) {
        bMap.set(b.id, {
          id: b.id,
          booking_number: b.booking_number,
          status: b.status,
          studioName: b.studios?.name || 'Unknown Studio',
          classes: [],
        });
      }
      bMap.get(b.id)!.classes.push({
        startTime: cls.start_time,
        endTime: cls.end_time,
        level: cls.class_level,
      });
    }
    return Array.from(bMap.values());
  }, [selectedDay, classes]);

  const totalBookings = useMemo(() => {
    const ids = new Set<string>();
    for (const cls of classes) {
      if (cls.bookings) ids.add(cls.bookings.id);
    }
    return ids.size;
  }, [classes]);

  // Build a deduplicated list of bookings with their classes, grouped by date
  const bookingsList = useMemo(() => {
    const bookingMap = new Map<
      string,
      {
        id: string;
        booking_number: string;
        status: string;
        studioName: string;
        classes: { date: string; startTime: string | null; endTime: string | null; level: string | null; students: number | null }[];
        tourStopName: string;
        cityName: string;
      }
    >();

    for (const cls of classes) {
      if (!cls.bookings) continue;
      const b = cls.bookings;
      if (statusFilter && b.status !== statusFilter) continue;
      if (!bookingMap.has(b.id)) {
        const ts = tourStops.find((t) => t.id === cls.tour_stop_id);
        bookingMap.set(b.id, {
          id: b.id,
          booking_number: b.booking_number,
          status: b.status,
          studioName: b.studios?.name || 'Unknown Studio',
          classes: [],
          tourStopName: ts?.shows?.name || '',
          cityName: ts?.cities?.name || '',
        });
      }
      bookingMap.get(b.id)!.classes.push({
        date: cls.class_date,
        startTime: cls.start_time,
        endTime: cls.end_time,
        level: cls.class_level,
        students: cls.expected_students,
      });
    }

    // Sort by earliest class date
    return Array.from(bookingMap.values()).sort((a, b) => {
      const aDate = a.classes[0]?.date || '';
      const bDate = b.classes[0]?.date || '';
      return aDate.localeCompare(bDate);
    });
  }, [classes, tourStops, statusFilter]);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Calendar</h1>
          <p className="text-warm-500 mt-1 text-sm">Bookings and tour dates at a glance.</p>
        </div>

        <div className="flex items-center gap-3 overflow-x-auto">
          {/* View Toggle */}
          <div className="flex bg-warm-100 rounded-lg p-0.5 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('bookings')}
              className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'bookings'
                  ? 'bg-white text-warm-900 shadow-sm'
                  : 'text-warm-500 hover:text-warm-700'
              }`}
            >
              Bookings
            </button>
            <button
              type="button"
              onClick={() => setViewMode('tour_stops')}
              className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'tour_stops'
                  ? 'bg-white text-warm-900 shadow-sm'
                  : 'text-warm-500 hover:text-warm-700'
              }`}
            >
              Tour Stops
            </button>
          </div>

          {/* Show Filter */}
          <select
            value={selectedShowId}
            onChange={(e) => setSelectedShowId(e.target.value)}
            className="px-3 py-1.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white shrink-0"
          >
            <option value="">All Shows</option>
            {shows.map((show) => (
              <option key={show.id} value={show.id}>
                {show.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
          {error}
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm px-5 py-3 mb-4 border border-warm-100">
        {viewMode === 'tour_stops' ? (
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {shows.map((show) => {
              const colors = showColorMap.get(show.id);
              return (
                <button
                  key={show.id}
                  type="button"
                  onClick={() =>
                    setSelectedShowId((prev) =>
                      prev === show.id ? '' : show.id,
                    )
                  }
                  className={`flex items-center gap-2 transition-opacity ${
                    selectedShowId && selectedShowId !== show.id
                      ? 'opacity-40'
                      : 'opacity-100'
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: colors?.bg }}
                  />
                  <span className="text-xs font-medium text-warm-600">
                    {show.name}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {Object.entries(BOOKING_STATUS_COLORS).map(([status, colors]) => (
              <button
                key={status}
                type="button"
                onClick={() =>
                  setStatusFilter((prev) => (prev === status ? '' : status))
                }
                className={`flex items-center gap-2 transition-opacity ${
                  statusFilter && statusFilter !== status
                    ? 'opacity-40'
                    : 'opacity-100'
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ backgroundColor: colors.bg }}
                />
                <span className="text-xs font-medium text-warm-600">
                  {statusLabelFor(status)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-5 border border-warm-100 relative overflow-x-auto">
        {isLoading && (
          <div className="absolute top-4 right-4 z-10">
            <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek',
          }}
          events={events}
          eventContent={renderEventContent}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          eventDisplay="block"
          dayMaxEvents={3}
          moreLinkClick={handleMoreLinkClick}
          height="auto"
          eventClassNames="!bg-transparent !border-0 !shadow-none"
          dayCellClassNames="hover:bg-warm-50/50 transition-colors"
        />
      </div>

      {/* Bookings List View */}
      <div className="bg-white rounded-xl shadow-sm border border-warm-100 mt-5">
        <div className="px-5 py-4 border-b border-warm-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-warm-900">
            Bookings List
          </h2>
          <span className="text-xs text-warm-400 font-medium">
            {bookingsList.length} booking{bookingsList.length !== 1 ? 's' : ''}
          </span>
        </div>

        {bookingsList.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm text-warm-400">No bookings in this period.</p>
          </div>
        ) : (
          <div className="divide-y divide-warm-50">
            {bookingsList.map((booking) => {
              const statusColors =
                BOOKING_STATUS_COLORS[booking.status] || BOOKING_STATUS_COLORS.pending!;
              const classDates = booking.classes
                .map((c) => formatDate(c.date))
                .join(', ');
              const firstClass = booking.classes[0];

              return (
                <Link
                  key={booking.id}
                  to={`/admin/bookings/${booking.id}`}
                  className="flex items-center gap-4 px-5 py-3.5 hover:bg-warm-50/60 transition-colors group"
                >
                  {/* Status dot */}
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: statusColors.bg }}
                  />

                  {/* Main info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-warm-900 group-hover:text-primary-700 truncate">
                        {booking.studioName}
                      </span>
                      <span className="text-[10px] text-warm-400 font-mono flex-shrink-0">
                        {booking.booking_number}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-warm-500">{classDates}</span>
                      {firstClass?.startTime && (
                        <span className="text-xs text-warm-400">
                          {formatTime(firstClass.startTime)}
                          {firstClass.endTime && ` – ${formatTime(firstClass.endTime)}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Show / City */}
                  {booking.tourStopName && (
                    <div className="hidden md:block text-right flex-shrink-0">
                      <p className="text-xs text-warm-500">{booking.tourStopName}</p>
                      {booking.cityName && (
                        <p className="text-[10px] text-warm-400">{booking.cityName}</p>
                      )}
                    </div>
                  )}

                  {/* Classes count */}
                  <span className="text-xs text-warm-400 flex-shrink-0 hidden sm:block">
                    {booking.classes.length} class{booking.classes.length !== 1 ? 'es' : ''}
                  </span>

                  {/* Status badge */}
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                    style={{
                      backgroundColor: statusColors.light,
                      color: statusColors.text,
                    }}
                  >
                    {statusLabelFor(booking.status)}
                  </span>

                  {/* Arrow */}
                  <svg
                    className="w-3.5 h-3.5 text-warm-300 group-hover:text-warm-500 flex-shrink-0 transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <div className="bg-white rounded-xl shadow-sm p-5 border border-warm-100">
          <p className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-1">Tour Stops</p>
          <p className="text-2xl font-display font-bold text-warm-900">{tourStops.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-warm-100">
          <p className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-1">Bookings</p>
          <p className="text-2xl font-display font-bold text-warm-900">{totalBookings}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-warm-100">
          <p className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-1">Classes</p>
          <p className="text-2xl font-display font-bold text-warm-900">{classes.length}</p>
        </div>
      </div>

      {/* Day Popup */}
      {selectedDay && (
        <>
          <div
            className="fixed inset-0 bg-warm-900/20 backdrop-blur-[2px] z-40 animate-fade-in"
            onClick={() => setSelectedDay(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDay(null)}>
            <div
              className="bg-white rounded-xl shadow-xl border border-warm-100 w-full max-w-md max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-warm-100 px-5 py-4 flex items-center justify-between z-10 rounded-t-xl">
                <h3 className="font-display font-semibold text-warm-900">
                  {formatDate(selectedDay)}
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedDay(null)}
                  className="p-1.5 text-warm-400 hover:text-warm-600 rounded-lg hover:bg-warm-100 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* Bookings on this day (shown first) */}
                {dayBookings.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2.5">
                      Bookings
                    </h4>
                    <div className="space-y-2">
                      {dayBookings.map((b) => {
                        const statusColors = BOOKING_STATUS_COLORS[b.status] || BOOKING_STATUS_COLORS.pending!;
                        return (
                          <Link
                            key={b.id}
                            to={`/admin/bookings/${b.id}`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-warm-100 hover:border-warm-200 hover:shadow-sm transition-all group"
                            onClick={() => setSelectedDay(null)}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: statusColors.bg }}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-warm-900 group-hover:text-primary-700 truncate">
                                {b.studioName}
                              </p>
                              <p className="text-xs text-warm-400 mt-0.5">{b.booking_number}</p>
                              {b.classes.map((c, i) => (
                                <p key={i} className="text-xs text-warm-400 mt-0.5">
                                  {c.startTime ? formatTime(c.startTime) : 'Time TBD'}
                                  {c.endTime && ` – ${formatTime(c.endTime)}`}
                                  {c.level && ` · ${c.level.replace(/_/g, ' ')}`}
                                </p>
                              ))}
                            </div>
                            <span
                              className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: statusColors.light, color: statusColors.text }}
                            >
                              {statusLabelFor(b.status)}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tour Stops on this day */}
                {dayTourStops.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2.5">
                      Tour Stops
                    </h4>
                    <div className="space-y-2">
                      {dayTourStops.map((ts) => {
                        const colors = showColorMap.get(ts.show_id) || SHOW_COLORS[0]!;
                        return (
                          <Link
                            key={ts.id}
                            to={`/admin/tour-stops/${ts.id}/edit`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-warm-100 hover:border-warm-200 hover:shadow-sm transition-all group"
                            onClick={() => setSelectedDay(null)}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: colors.bg }}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-warm-900 group-hover:text-primary-700 truncate">
                                {ts.shows?.name || 'Unknown Show'}
                              </p>
                              <p className="text-xs text-warm-500 mt-0.5">
                                {ts.cities?.name}, {ts.cities?.state} · {ts.theaters?.name}
                              </p>
                              <p className="text-[10px] text-warm-400 mt-0.5">
                                {formatShortDate(ts.start_date)} – {formatShortDate(ts.end_date)}
                              </p>
                            </div>
                            {ts.is_sold_out && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 uppercase">
                                Sold Out
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {dayTourStops.length === 0 && dayBookings.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-warm-400">Nothing scheduled for this day.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail Panel Overlay */}
      {selectedEvent && (
        <>
          <div
            className="fixed inset-0 bg-warm-900/20 backdrop-blur-[2px] z-40 animate-fade-in"
            onClick={() => setSelectedEvent(null)}
          />

          <div
            className="fixed inset-y-0 right-0 w-full sm:w-[400px] bg-white shadow-xl z-50 overflow-y-auto border-l border-warm-200"
            style={{ animation: 'slide-in-right 0.2s ease-out' }}
          >
            {/* Panel Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-warm-100 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-display font-semibold text-warm-900">
                {selectedEvent.type === 'tour_stop'
                  ? 'Tour Stop'
                  : 'Booking'}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="p-1.5 text-warm-400 hover:text-warm-600 rounded-lg hover:bg-warm-100 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {selectedEvent.type === 'tour_stop' && selectedEvent.tourStop && (
                <TourStopPanel
                  tourStop={selectedEvent.tourStop}
                  bookings={bookingsByTourStop.get(selectedEvent.tourStop.id)}
                  showColor={showColorMap.get(selectedEvent.tourStop.show_id)}
                />
              )}

              {selectedEvent.type === 'class' && selectedEvent.classItem && (
                <ClassPanel
                  classItem={selectedEvent.classItem}
                  tourStop={tourStops.find(
                    (ts) => ts.id === selectedEvent.classItem!.tour_stop_id,
                  )}
                  showColorMap={showColorMap}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Tour Stop Detail Panel
// =============================================================================

function TourStopPanel({
  tourStop,
  bookings,
  showColor,
}: {
  tourStop: CalendarTourStop;
  bookings?: Map<
    string,
    { booking: NonNullable<CalendarClass['bookings']>; classes: CalendarClass[] }
  >;
  showColor?: (typeof SHOW_COLORS)[0];
}) {
  const bookingList = bookings ? Array.from(bookings.values()) : [];

  return (
    <div className="space-y-5">
      {/* Show Header */}
      <div
        className="p-4 rounded-lg"
        style={{ backgroundColor: showColor?.light || '#EFF6FF' }}
      >
        <div className="flex items-center gap-2.5 mb-2">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: showColor?.bg || '#3B82F6' }}
          />
          <span
            className="text-base font-display font-bold"
            style={{ color: showColor?.text || '#1E40AF' }}
          >
            {tourStop.shows?.name || 'Unknown Show'}
          </span>
        </div>
        <div className="ml-5.5 space-y-0.5">
          <p className="text-sm" style={{ color: showColor?.text || '#1E40AF', opacity: 0.8 }}>
            {tourStop.cities?.name}, {tourStop.cities?.state}
          </p>
          <p className="text-xs" style={{ color: showColor?.text || '#1E40AF', opacity: 0.6 }}>
            {tourStop.theaters?.name}
          </p>
        </div>
      </div>

      {/* Date range */}
      <div className="flex items-center gap-3 px-1">
        <svg className="w-4 h-4 text-warm-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
        <span className="text-sm text-warm-700">
          {formatDate(tourStop.start_date)} – {formatDate(tourStop.end_date)}
        </span>
        {tourStop.is_sold_out && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 uppercase tracking-wide">
            Sold Out
          </span>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-warm-100" />

      {/* Bookings */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-warm-500 uppercase tracking-wide">
            Bookings
          </h4>
          <span className="text-xs font-medium text-warm-400">
            {bookingList.length}
          </span>
        </div>

        {bookingList.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-warm-200 rounded-lg">
            <p className="text-sm text-warm-400">No bookings yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {bookingList.map(({ booking, classes: bookingClasses }) => {
              const statusColors = BOOKING_STATUS_COLORS[booking.status] || BOOKING_STATUS_COLORS.pending!;
              return (
                <Link
                  key={booking.id}
                  to={`/admin/bookings/${booking.id}`}
                  className="block p-3 rounded-lg border border-warm-100 hover:border-warm-200 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-warm-900 group-hover:text-primary-700 truncate">
                        {booking.studios?.name || 'Unknown Studio'}
                      </p>
                      <p className="text-xs text-warm-400 mt-0.5">
                        {booking.booking_number} · {bookingClasses.length} class
                        {bookingClasses.length !== 1 ? 'es' : ''}
                      </p>
                    </div>
                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                      style={{
                        backgroundColor: statusColors.light,
                        color: statusColors.text,
                      }}
                    >
                      {statusLabelFor(booking.status)}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer link */}
      <Link
        to={`/admin/tour-stops/${tourStop.id}/edit`}
        className="flex items-center justify-center gap-2 text-sm text-warm-600 hover:text-warm-900 font-medium py-2.5 border border-warm-200 rounded-lg hover:bg-warm-50 transition-all"
      >
        View Tour Stop
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
        </svg>
      </Link>
    </div>
  );
}

// =============================================================================
// Class / Booking Detail Panel
// =============================================================================

function ClassPanel({
  classItem,
  tourStop,
  showColorMap,
}: {
  classItem: CalendarClass;
  tourStop?: CalendarTourStop;
  showColorMap: Map<string, (typeof SHOW_COLORS)[0]>;
}) {
  const booking = classItem.bookings;
  const showColor = tourStop ? showColorMap.get(tourStop.show_id) : undefined;
  const statusColors = booking
    ? BOOKING_STATUS_COLORS[booking.status] || BOOKING_STATUS_COLORS.pending!
    : BOOKING_STATUS_COLORS.pending!;

  return (
    <div className="space-y-5">
      {/* Studio / Booking header */}
      <div>
        {booking?.studios ? (
          <Link
            to={`/admin/studios/${booking.studios.id}`}
            className="text-base font-display font-bold text-warm-900 hover:text-primary-700 transition-colors"
          >
            {booking.studios.name}
          </Link>
        ) : (
          <p className="text-base font-display font-bold text-warm-900">Unknown Studio</p>
        )}
        {booking && (
          <div className="flex items-center gap-2 mt-1.5">
            <Link
              to={`/admin/bookings/${booking.id}`}
              className="text-xs text-warm-400 font-mono hover:text-primary-700 transition-colors"
            >
              {booking.booking_number}
            </Link>
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{
                backgroundColor: statusColors.light,
                color: statusColors.text,
              }}
            >
              {statusLabelFor(booking.status)}
            </span>
          </div>
        )}
      </div>

      {/* Tour Stop context */}
      {tourStop && (
        <div
          className="p-3 rounded-lg"
          style={{ backgroundColor: showColor?.light || '#F5F5F4' }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: showColor?.bg || '#3B82F6' }}
            />
            <span
              className="text-sm font-semibold"
              style={{ color: showColor?.text || '#44403C' }}
            >
              {tourStop.shows?.name || 'Unknown Show'}
            </span>
          </div>
          <p
            className="text-xs ml-4.5"
            style={{ color: showColor?.text || '#44403C', opacity: 0.7 }}
          >
            {tourStop.cities?.name}, {tourStop.cities?.state} · {tourStop.theaters?.name}
          </p>
          <p
            className="text-xs ml-4.5 mt-0.5"
            style={{ color: showColor?.text || '#44403C', opacity: 0.5 }}
          >
            {formatDate(tourStop.start_date)} – {formatDate(tourStop.end_date)}
          </p>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-warm-100" />

      {/* Class info */}
      <div>
        <h4 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-3">
          Class Details
        </h4>
        <div className="space-y-2.5">
          <InfoRow
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            }
            label="Date"
            value={formatDate(classItem.class_date)}
          />
          {(classItem.start_time || classItem.end_time) && (
            <InfoRow
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              label="Time"
              value={`${formatTime(classItem.start_time)} – ${formatTime(classItem.end_time)}`}
            />
          )}
          {classItem.class_level && (
            <InfoRow
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              }
              label="Level"
              value={classItem.class_level.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            />
          )}
          {classItem.expected_students && (
            <InfoRow
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              }
              label="Students"
              value={String(classItem.expected_students)}
            />
          )}
          <InfoRow
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            }
            label="Faculty"
            value={
              classItem.faculty ? (
                <Link
                  to={`/admin/faculty/${classItem.faculty.id}`}
                  className="text-warm-900 font-medium hover:text-primary-700 transition-colors"
                >
                  {classItem.faculty.first_name} {classItem.faculty.last_name}
                </Link>
              ) : (
                <span className="text-warm-400">Unassigned</span>
              )
            }
          />
        </div>
      </div>

      {/* Footer link */}
      {booking && (
        <Link
          to={`/admin/bookings/${booking.id}`}
          className="flex items-center justify-center gap-2 text-sm text-warm-600 hover:text-warm-900 font-medium py-2.5 border border-warm-200 rounded-lg hover:bg-warm-50 transition-all"
        >
          View Full Booking
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      )}
    </div>
  );
}

// =============================================================================
// Shared
// =============================================================================

function InfoRow({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      {icon && <span className="text-warm-400 flex-shrink-0">{icon}</span>}
      <span className="text-warm-500">{label}</span>
      <span className="ml-auto text-warm-900 font-medium text-right">{value}</span>
    </div>
  );
}
